import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import {
  NotesTextEditorHandle,
  OneAlert,
  RichTextDisplay,
} from '@factorialco/f0-react/dist/experimental'
import { Check, LayersFront, Plus } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

interface AiChatMessageProps {
  isAssistant: boolean
  content: string
  textEditorRef?: React.RefObject<NotesTextEditorHandle> | null
  isError?: boolean
  rawMessage?: string
}

export const AiChatMessage = ({
  isAssistant,
  content,
  textEditorRef,
  isError = false,
  rawMessage,
}: AiChatMessageProps) => {
  const [isHovering, setIsHovering] = useState(false)
  const [isCopying, setIsCopying] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const onCopy = () => {
    if (isCopying) return
    setIsCopying(true)
    // Create a temporary div to parse the HTML and extract plain text
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = content
    const plainText = tempDiv.textContent || tempDiv.innerText || content
    navigator.clipboard.writeText(plainText)
    setTimeout(() => {
      setIsCopying(false)
    }, 500)
  }

  const onSave = () => {
    if (isSaving) return
    setIsSaving(true)
    if (textEditorRef) {
      const parsedRawMessage = JSON.parse(rawMessage || '{}')
      textEditorRef.current?.pushContent(parsedRawMessage.content || content)
    }
    setTimeout(() => {
      setIsSaving(false)
    }, 500)
  }

  return (
    <Box
      alignItems={isAssistant ? 'flexStart' : 'flexEnd'}
      paddingBottom={isAssistant ? 's12' : 's24'}
      gap="s4"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <Box
        background={isAssistant ? 'grey000White' : 'grey200'}
        paddingX={isAssistant ? 's0' : 's16'}
        paddingY={isAssistant ? 's0' : 's12'}
        border={
          isAssistant
            ? undefined
            : {
                all: {
                  width: 's1',
                  style: 'solid',
                  color: 'grey300',
                },
              }
        }
        style={{
          maxWidth: isAssistant ? '100%' : '80%',
          borderRadius: isAssistant ? '0' : '24px',
        }}
      >
        {isError ? (
          <OneAlert title={content} variant="critical" description={''} />
        ) : (
          <RichTextDisplay content={content} />
        )}
      </Box>
      {isAssistant && !isError && (
        <Box
          flexDirection="row"
          style={{
            opacity: isHovering ? '1' : '0',
            transition: 'opacity 0.2s ease-in-out',
          }}
          gap="s4"
        >
          {textEditorRef && (
            <F0Button
              label={i18n.t('common.save')}
              onClick={onSave}
              size="sm"
              variant="outline"
              icon={isSaving ? Check : Plus}
              loading={isSaving}
            />
          )}
          <F0Button
            label={i18n.t('common.copy')}
            onClick={() => onCopy()}
            size="sm"
            hideLabel
            variant="ghost"
            icon={isCopying ? Check : LayersFront}
            loading={isCopying}
          />
        </Box>
      )}
    </Box>
  )
}
